// Zoom-proof geometry for Modal UI v2 — modals that sit BELOW the global sub-header and finish just above the
// bottom of the screen, on any screen size.
//
// Why this exists: on laptops and iPads in landscape the whole page is zoomed to 0.8 (utils/desktopZoomFix.js).
// A modal's `top` is a CSS length, so it is scaled by that zoom, but a measurement taken with
// getBoundingClientRect() is reported on screen (already scaled) — using one as the other scales it twice and the
// modal starts too high, up under the sub-header buttons. `100vh` has the same problem, so a modal capped at
// `calc(100vh - top - margin)` stops short of (or overshoots) the bottom of the screen.
//
// The fix does not guess how a given browser reports zoomed geometry. It measures it: an invisible, zero-width
// box pinned to exactly the viewport (top: 0; bottom: 0). The browser reports that box's height both on screen
// (getBoundingClientRect) and in the page's own CSS pixels (offsetHeight); the ratio is the real page zoom, and
// the CSS-pixel height is the viewport height in the page's own pixels. If a browser reports both the same the
// ratio is 1 and nothing is changed.
//
// The second half of this file (markModalPanelsWithin / watchModalPanels / MODAL_VIEWPORT_CSS) makes modal panels
// fill the space between the sub-header and the bottom of the screen, using a percentage of the modal overlay
// instead of any viewport unit.

// Style for the probe element (rendered once, by AppHeader).
export const VIEWPORT_PROBE_STYLE = {
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  width: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
};

const round2 = (n) => Math.round(n * 100) / 100;

// probe:     the invisible viewport-tall element described above (may be missing — falls back to window.innerHeight)
// endMarker: the zero-height element that sits directly under the sub-header
// Returns { zoom, viewportHeight, bottom } where
//   viewportHeight = the viewport's height in the page's own CSS pixels (use instead of 100vh)
//   bottom         = where the sub-header ends, in the page's own CSS pixels (use as a modal's `top`)
export const computeModalViewport = (probe, endMarker) => {
  let viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  let zoom = 1;
  if (probe && probe.offsetHeight > 0) {
    viewportHeight = probe.offsetHeight;
    const ratio = probe.getBoundingClientRect().height / probe.offsetHeight;
    if (ratio > 0.25 && ratio < 4 && Math.abs(ratio - 1) > 0.005) zoom = ratio;
  }
  const shownBottom = endMarker ? endMarker.getBoundingClientRect().bottom : 0;
  return { zoom, viewportHeight: round2(viewportHeight), bottom: round2(shownBottom / zoom) };
};

// Publishes the two numbers as CSS variables on <html>, so CSS can use them anywhere (and update live, without a
// React re-render, when the window is resized / rotated or the zoom is applied):
//   --c30-vh         viewport height in the page's own CSS pixels   (falls back to 100vh when absent)
//   --c30-modal-top  where the sub-header ends, in the same pixels
export const applyModalViewportVars = ({ bottom, viewportHeight }, root) => {
  const el = root || (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return;
  el.style.setProperty('--c30-vh', `${viewportHeight}px`);
  el.style.setProperty('--c30-modal-top', `${bottom}px`);
};

// ── Modal panels: fill the space between the sub-header and the bottom of the screen ─────────────────────────────
// Every Modal UI v2 modal is a dimmed overlay (`position: fixed`, `z-index: 45`, `top` = the sub-header's bottom,
// `bottom: 0`) holding one panel that is capped with an inline `max-height: calc(100vh - <top> - <margin>)` (the
// original Customer Management pattern, copied into several modals, and the shared helper in utils/modalUiV2.js).
// That `100vh` cap is the part that does not survive the page zoom. But the overlay itself is always the right
// size — it runs from just under the sub-header to the bottom of the screen at any zoom — so the panel simply
// needs to fill it: `max-height: 100%` of the overlay's own box. A percentage of a box has no viewport units in it,
// so it is right at any zoom and any screen size, and the overlay's own padding keeps the small gap at the bottom.
//
// The panels are found by reading the real style values (CSSOM), not by matching the style attribute's text
// (browsers write calc() text differently), and are tagged with data-c30-modal-panel.
const PANEL_ATTR = 'data-c30-modal-panel';

const hasCalcMaxHeight = (el) => !!el && !!el.style && /calc\(/.test(el.style.maxHeight || '');
const isModalOverlay = (el) => !!el && !!el.style && el.style.zIndex === '45';

// Tags `el` when it is the calc()-capped panel of a modal overlay, and un-tags it when it no longer is
// (e.g. React reused the element for something else, or the overlay's / panel's styles changed).
const syncPanel = (el) => {
  if (!el || el.nodeType !== 1) return;
  if (isModalOverlay(el.parentElement) && hasCalcMaxHeight(el)) el.setAttribute(PANEL_ATTR, '1');
  else if (el.hasAttribute(PANEL_ATTR)) el.removeAttribute(PANEL_ATTR);
};

// Brings the tags in / around `node` up to date: every overlay's panel is tagged, stale tags are removed.
export const markModalPanelsWithin = (node) => {
  if (!node || node.nodeType !== 1) return;
  const elements = [node, ...node.querySelectorAll(`[style], [${PANEL_ATTR}]`)];
  elements.forEach((el) => {
    if (isModalOverlay(el)) Array.from(el.children).forEach(syncPanel);
    syncPanel(el);
  });
};

// Tags what is on the page now, then keeps the tags right as modals open and close and as their styles change.
// Returns a function that stops watching.
export const watchModalPanels = () => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined' || !document.body) return () => {};
  markModalPanelsWithin(document.body);
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'childList') {
        record.addedNodes.forEach(markModalPanelsWithin);
      } else if (record.type === 'attributes') {
        const target = record.target;
        syncPanel(target); // its own panel status may have changed
        // it may be an overlay (or have stopped being one): only look at children that can matter
        const children = isModalOverlay(target)
          ? Array.from(target.children)
          : Array.from(target.querySelectorAll(`:scope > [${PANEL_ATTR}]`));
        children.forEach(syncPanel);
      }
    });
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  return () => observer.disconnect();
};

export const MODAL_VIEWPORT_CSS = `
[data-c30-modal-panel] {
  max-height: 100% !important;
}
`;

// ── Page zoom ─────────────────────────────────────────────────────────────────────────────────────────────────────
// The page's zoom factor (1 on a normal screen, 0.8 on a laptop / iPad where the whole dashboard is zoomed out), measured
// the same way computeModalViewport() measures it: with an invisible viewport-tall probe. Divide getBoundingClientRect()
// values by it to get the page's own CSS pixels, which is what `position: fixed` top / left / width need — so a search
// dropdown lands exactly under its input at any zoom. The probe is created once and reused.
let zoomProbe = null;
export const getPageZoom = () => {
  if (typeof document === 'undefined' || !document.body) return 1;
  if (!zoomProbe || !zoomProbe.isConnected) {
    zoomProbe = document.createElement('div');
    Object.assign(zoomProbe.style, VIEWPORT_PROBE_STYLE);
    zoomProbe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(zoomProbe);
  }
  if (!(zoomProbe.offsetHeight > 0)) return 1;
  const ratio = zoomProbe.getBoundingClientRect().height / zoomProbe.offsetHeight;
  return ratio > 0.25 && ratio < 4 && Math.abs(ratio - 1) > 0.005 ? ratio : 1;
};
